/**
 * ZAPHUB 2.1 — ExecutionTimeline
 *
 * Visual timeline of workflow executions with per-node breakdown.
 * Fetches from GET /api/workflows/:id/executions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  CheckCircle,
  XCircle,
  Clock,
  Play,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Activity,
  Zap,
  AlertCircle,
} from 'lucide-react';
import api from '../services/apiClient';

interface NodeExecution {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: 'SUCCESS' | 'FAILED' | 'SKIPPED';
  durationMs: number;
  startedAt: string;
  error?: string;
  output?: any;
}

interface Execution {
  id: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'PENDING';
  startedAt: string;
  completedAt?: string;
  duration?: number;
  error?: string;
  triggeredBy?: string;
  nodeResults?: NodeExecution[];
}

interface ExecutionTimelineProps {
  workflowId?: string;
  limit?: number;
}

function statusColor(status: string) {
  switch (status) {
    case 'COMPLETED': return 'text-green-600 bg-green-50 border-green-200';
    case 'FAILED': return 'text-red-600 bg-red-50 border-red-200';
    case 'RUNNING': return 'text-blue-600 bg-blue-50 border-blue-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'COMPLETED': return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'FAILED': return <XCircle className="w-4 h-4 text-red-600" />;
    case 'RUNNING': return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
    default: return <Clock className="w-4 h-4 text-gray-500" />;
  }
}

function formatDuration(ms?: number): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60000)}m`;
}

function NodeBar({ node, totalMs }: { node: NodeExecution; totalMs: number }) {
  const pct = totalMs > 0 ? (node.durationMs / totalMs) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-4 flex-shrink-0">
        {node.status === 'SUCCESS' && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
        {node.status === 'FAILED' && <XCircle className="w-3.5 h-3.5 text-red-500" />}
        {node.status === 'SKIPPED' && <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300" />}
      </div>
      <div className="w-32 flex-shrink-0">
        <div className="text-xs font-medium text-gray-800 truncate">{node.nodeName || node.nodeId}</div>
        <div className="text-xs text-gray-400">{node.nodeType}</div>
      </div>
      <div className="flex-1">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              node.status === 'FAILED' ? 'bg-red-400' :
              node.status === 'SKIPPED' ? 'bg-gray-300' :
              'bg-green-400'
            }`}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        </div>
      </div>
      <div className="w-12 text-right text-xs text-gray-500">{formatDuration(node.durationMs)}</div>
      {node.error && (
        <div className="w-4" aria-label={node.error}>
          <AlertCircle className="w-3.5 h-3.5 text-red-400" />
        </div>
      )}
    </div>
  );
}

function ExecutionRow({ execution }: { execution: Execution }) {
  const [expanded, setExpanded] = useState(false);
  const hasNodes = execution.nodeResults && execution.nodeResults.length > 0;

  return (
    <div className={`border rounded-xl mb-3 overflow-hidden ${statusColor(execution.status)}`}>
      {/* Header row */}
      <div
        className={`flex items-center gap-4 px-4 py-3 cursor-pointer ${hasNodes ? 'hover:bg-black/5' : ''}`}
        onClick={() => hasNodes && setExpanded(!expanded)}
      >
        <StatusIcon status={execution.status} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {new Date(execution.startedAt).toLocaleString('es-ES', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
              })}
            </span>
            <Badge variant="outline" className="text-xs">{execution.status}</Badge>
          </div>
          {execution.triggeredBy && (
            <div className="text-xs opacity-70">por {execution.triggeredBy}</div>
          )}
          {execution.error && (
            <div className="text-xs text-red-700 mt-0.5 truncate">{execution.error}</div>
          )}
        </div>

        <div className="flex items-center gap-4 text-sm">
          {execution.duration && (
            <div className="flex items-center gap-1 text-xs opacity-70">
              <Clock className="w-3.5 h-3.5" />
              {formatDuration(execution.duration)}
            </div>
          )}
          {hasNodes && (
            expanded ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />
          )}
        </div>
      </div>

      {/* Node breakdown */}
      {expanded && hasNodes && (
        <div className="px-4 pb-3 pt-1 bg-white/60">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Desglose por nodo
          </div>
          {execution.nodeResults!.map(node => (
            <NodeBar
              key={node.nodeId}
              node={node}
              totalMs={execution.duration || execution.nodeResults!.reduce((s, n) => s + n.durationMs, 0)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ExecutionTimeline({ workflowId, limit = 20 }: ExecutionTimelineProps) {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<{ total: number; success: number; failed: number; avgDuration: number } | null>(null);

  const load = useCallback(async () => {
    if (!workflowId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workflows/${workflowId}/executions?limit=${limit}`);
      const execs: Execution[] = data.data?.executions || data.data || [];
      setExecutions(execs);

      // Compute stats
      const completed = execs.filter(e => e.status === 'COMPLETED');
      const failed = execs.filter(e => e.status === 'FAILED');
      const avgDuration = completed.reduce((s, e) => s + (e.duration || 0), 0) / (completed.length || 1);
      setStats({
        total: execs.length,
        success: completed.length,
        failed: failed.length,
        avgDuration,
      });
    } catch {
      setExecutions([]);
    } finally {
      setLoading(false);
    }
  }, [workflowId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  if (!workflowId) {
    return (
      <Card className="p-8 text-center text-gray-400">
        <Activity className="w-12 h-12 mx-auto mb-3 opacity-20" />
        <p>Selecciona un workflow para ver su timeline de ejecución</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          <Card className="p-3 text-center">
            <div className="text-xl font-bold text-gray-800">{stats.total}</div>
            <div className="text-xs text-gray-400">ejecuciones</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-xl font-bold text-green-700">{stats.success}</div>
            <div className="text-xs text-gray-400">exitosas</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-xl font-bold text-red-600">{stats.failed}</div>
            <div className="text-xs text-gray-400">fallidas</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-xl font-bold text-blue-700">{formatDuration(stats.avgDuration)}</div>
            <div className="text-xs text-gray-400">tiempo promedio</div>
          </Card>
        </div>
      )}

      {/* Controls */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-800">
          {loading ? 'Cargando...' : `${executions.length} ejecuciones recientes`}
        </h3>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {/* Timeline */}
      {loading && executions.length === 0 && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
        </div>
      )}

      {!loading && executions.length === 0 && (
        <Card className="p-12 text-center text-gray-400">
          <Play className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p>Aún no hay ejecuciones para este workflow</p>
        </Card>
      )}

      {executions.map(exec => (
        <ExecutionRow key={exec.id} execution={exec} />
      ))}

      {executions.length > 0 && executions.length >= limit && (
        <div className="text-center">
          <p className="text-xs text-gray-400">Mostrando las últimas {limit} ejecuciones</p>
        </div>
      )}
    </div>
  );
}
